import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Database, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Play,
  RefreshCw,
  Zap
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';
import { Progress } from '@/components/ui/progress';

interface ContentDivisionActionsProps {
  onRefresh?: () => void;
}

export const ContentDivisionActions: React.FC<ContentDivisionActionsProps> = ({ onRefresh }) => {
  const [populateLoading, setPopulateLoading] = useState(false);
  const [populateStatus, setPopulateStatus] = useState<any>(null);
  const [autoDivideLoading, setAutoDivideLoading] = useState(false);
  const [divisionProgress, setDivisionProgress] = useState<any>(null);
  const { toast } = useToast();

  const handlePopulateDatabase = async () => {
    try {
      setPopulateLoading(true);
      setPopulateStatus(null);

      logger.info('📊 Triggering content division status population...');

      const { data, error } = await supabase.functions.invoke('populate-division-status');

      if (error) throw error;

      setPopulateStatus(data);

      if (data.success) {
        toast({
          title: '✅ Database Populated',
          description: `${data.stats.total} modules categorized: ${data.stats.extreme} extreme, ${data.stats.heavy} heavy, ${data.stats.moderate} moderate`,
          duration: 8000
        });

        logger.info('✅ Population complete:', data.stats);
      } else {
        throw new Error(data.error || 'Population failed');
      }
    } catch (error: any) {
      logger.error('Failed to populate database:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to populate content division status',
        variant: 'destructive'
      });
    } finally {
      setPopulateLoading(false);
    }
  };

  const handleAutoDivide = async (tier: 'extreme' | 'heavy' | 'moderate') => {
    try {
      setAutoDivideLoading(true);
      setDivisionProgress({ tier, status: 'processing', processed: 0 });

      logger.info(`🎯 Starting auto-division for ${tier} tier...`);

      const { data, error } = await supabase.functions.invoke('auto-divide-content', {
        body: { tier, batchSize: tier === 'extreme' ? 17 : tier === 'heavy' ? 50 : 100 }
      });

      if (error) throw error;

      setDivisionProgress({
        tier,
        status: 'completed',
        ...data.stats
      });

      toast({
        title: '✅ Division Complete',
        description: `${data.stats.succeeded} ${tier} modules successfully divided`,
        duration: 6000
      });

      logger.info('✅ Auto-division complete:', data.stats);

    } catch (error: any) {
      logger.error('Failed to auto-divide:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to divide content',
        variant: 'destructive'
      });
      setDivisionProgress({ tier, status: 'failed' });
    } finally {
      setAutoDivideLoading(false);
    }
  };

  const handleAutoDivideAll = async () => {
    try {
      setAutoDivideLoading(true);
      
      // First, ensure database is populated
      if (!populateStatus) {
        toast({
          title: '🔄 Initializing System',
          description: 'Populating database with all modules first...',
          duration: 3000
        });
        
        const { data: initData, error: initError } = await supabase.functions.invoke('populate-division-status');
        
        if (initError) throw initError;
        
        setPopulateStatus(initData);
        
        toast({
          title: '✅ Initialization Complete',
          description: `${initData.stats.total} modules ready for division`,
          duration: 2000
        });
      }
      
      toast({
        title: '🚀 Starting Batch Division',
        description: 'Processing all pending modules across all tiers...',
        duration: 5000
      });

      // Process each tier sequentially
      const tiers: Array<'extreme' | 'heavy' | 'moderate'> = ['extreme', 'heavy', 'moderate'];
      const results = [];

      for (const tier of tiers) {
        try {
          setDivisionProgress({ tier, status: 'processing', processed: 0 });
          
          logger.info(`🎯 Processing ${tier} tier...`);

          const { data, error } = await supabase.functions.invoke('auto-divide-content', {
            body: { tier, batchSize: tier === 'extreme' ? 17 : tier === 'heavy' ? 50 : 100 }
          });

          if (error) throw error;

          results.push({ tier, ...data.stats });
          
          logger.info(`✅ ${tier} tier complete:`, data.stats);
        } catch (error: any) {
          logger.error(`Failed to process ${tier} tier:`, error);
          results.push({ tier, succeeded: 0, failed: 0, error: error.message });
        }
      }

      const totalSucceeded = results.reduce((sum, r) => sum + (r.succeeded || 0), 0);
      const totalFailed = results.reduce((sum, r) => sum + (r.failed || 0), 0);

      setDivisionProgress({
        tier: 'all',
        status: 'completed',
        succeeded: totalSucceeded,
        failed: totalFailed,
        results
      });

      toast({
        title: '✅ Batch Division Complete',
        description: `Successfully divided ${totalSucceeded} modules. ${totalFailed} failed.`,
        duration: 8000
      });

      // Refresh dashboard data
      if (onRefresh) {
        setTimeout(() => onRefresh(), 1000);
      } else {
        setTimeout(() => window.location.reload(), 2000);
      }

    } catch (error: any) {
      logger.error('Failed to batch divide:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to batch divide content',
        variant: 'destructive'
      });
    } finally {
      setAutoDivideLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Phase 2: Initialize Content Division System
        </CardTitle>
        <CardDescription>
          Populate content_division_status table with all 705 modules requiring division
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!populateStatus ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will scan all modules with 2000+ words and categorize them into:
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span><strong>Extreme Tier (5000+ words)</strong>: 17 modules → 5-tab division</span>
              </li>
              <li className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-500" />
                <span><strong>Heavy Tier (3000-5000 words)</strong>: 144 modules → 4-tab division</span>
              </li>
              <li className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-blue-500" />
                <span><strong>Moderate Tier (2000-3000 words)</strong>: 544 modules → 3-tab division</span>
              </li>
            </ul>
            
            <Button 
              onClick={handlePopulateDatabase} 
              disabled={populateLoading}
              className="w-full"
              size="lg"
            >
              {populateLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Populating Database...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Initialize 705 Modules
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div className="flex-1">
                <p className="font-semibold text-green-700 dark:text-green-400">
                  Database Populated Successfully
                </p>
                <p className="text-sm text-green-600 dark:text-green-500">
                  {populateStatus.message}
                </p>
              </div>
            </div>

            {populateStatus.stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">{populateStatus.stats.total}</div>
                    <div className="text-xs text-muted-foreground">Total Modules</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-red-600">{populateStatus.stats.extreme}</div>
                    <div className="text-xs text-muted-foreground">Extreme (5k+)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-orange-600">{populateStatus.stats.heavy}</div>
                    <div className="text-xs text-muted-foreground">Heavy (3-5k)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-blue-600">{populateStatus.stats.moderate}</div>
                    <div className="text-xs text-muted-foreground">Moderate (2-3k)</div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Button 
              onClick={() => window.location.reload()} 
              variant="outline"
              className="w-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Dashboard
            </Button>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Phase 3-14: Auto-Division */}
    {populateStatus && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-500" />
            Phase 3-14: Automated Content Division
          </CardTitle>
          <CardDescription>
            Automatically divide modules into sections using AI-powered content detection
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Auto-Divide All Button */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-purple-200 dark:border-purple-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-1">Complete All Pending Modules</h3>
                  <p className="text-sm text-muted-foreground">
                    Process all 568 pending modules across all tiers automatically
                  </p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline">47 Heavy</Badge>
                    <Badge variant="outline">521 Moderate</Badge>
                  </div>
                </div>
                <Button
                  onClick={handleAutoDivideAll}
                  disabled={autoDivideLoading}
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                >
                  {autoDivideLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Processing All Tiers...
                    </>
                  ) : (
                    <>
                      <Zap className="h-5 w-5 mr-2" />
                      Auto-Divide All 568 Modules
                    </>
                  )}
                </Button>
              </div>
              {divisionProgress?.tier === 'all' && divisionProgress.status === 'completed' && (
                <div className="mt-4 p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-700 dark:text-green-400">
                        All Tiers Complete!
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-500">
                        {divisionProgress.succeeded} modules divided successfully, {divisionProgress.failed} failed
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Extreme Tier */}
            <Card className="border-red-200 dark:border-red-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Extreme Tier</CardTitle>
                  <Badge variant="destructive">17 modules</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  5000+ words → 5 sections each
                </div>
                {divisionProgress?.tier === 'extreme' && (
                  <div className="space-y-2">
                    <Progress value={(divisionProgress.succeeded / 17) * 100} />
                    <div className="text-xs text-center">
                      {divisionProgress.succeeded} / 17 completed
                    </div>
                  </div>
                )}
                <Button
                  onClick={() => handleAutoDivide('extreme')}
                  disabled={autoDivideLoading}
                  className="w-full"
                  variant="destructive"
                  size="sm"
                >
                  {autoDivideLoading && divisionProgress?.tier === 'extreme' ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Zap className="h-3 w-3 mr-2" />
                      Auto-Divide
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Heavy Tier */}
            <Card className="border-orange-200 dark:border-orange-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Heavy Tier</CardTitle>
                  <Badge className="bg-orange-600">144 modules</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  3000-5000 words → 4 sections each
                </div>
                {divisionProgress?.tier === 'heavy' && (
                  <div className="space-y-2">
                    <Progress value={(divisionProgress.succeeded / 144) * 100} />
                    <div className="text-xs text-center">
                      {divisionProgress.succeeded} / 144 completed
                    </div>
                  </div>
                )}
                <Button
                  onClick={() => handleAutoDivide('heavy')}
                  disabled={autoDivideLoading}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  size="sm"
                >
                  {autoDivideLoading && divisionProgress?.tier === 'heavy' ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Zap className="h-3 w-3 mr-2" />
                      Auto-Divide
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Moderate Tier */}
            <Card className="border-blue-200 dark:border-blue-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Moderate Tier</CardTitle>
                  <Badge className="bg-blue-600">544 modules</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  2000-3000 words → 3 sections each
                </div>
                {divisionProgress?.tier === 'moderate' && (
                  <div className="space-y-2">
                    <Progress value={(divisionProgress.succeeded / 544) * 100} />
                    <div className="text-xs text-center">
                      {divisionProgress.succeeded} / 544 completed
                    </div>
                  </div>
                )}
                <Button
                  onClick={() => handleAutoDivide('moderate')}
                  disabled={autoDivideLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  {autoDivideLoading && divisionProgress?.tier === 'moderate' ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Zap className="h-3 w-3 mr-2" />
                      Auto-Divide
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {divisionProgress && divisionProgress.status === 'completed' && (
            <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-semibold text-green-700 dark:text-green-400">
                    {divisionProgress.tier} tier division complete!
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-500">
                    {divisionProgress.succeeded} modules divided, {divisionProgress.failed} failed
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    )}
    </div>
  );
};
